import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
// Components
import { Box, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { NoneAvailableMessage, WizardCheckbox } from 'common';

// Context
import { SequencingWizardContext } from '../SequencingWizardContext';

const notEmpty = R.complement(R.isEmpty);

const Header = styled(Typography)(({ theme }) => ({
  margin: theme.spacing(3, 3, 2),
}));

const LocationCheckboxList = props => {
  const { fetchLocations, locationsLoading, onChange, locations, title } = props;
  const theme = useTheme();
  const boxProps = {
    alignItems: 'center',
    borderBottom: 1,
    color: 'graphic.light',
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 3),
    width: '100%',
  };

  const {
    data: {
      currentAffiliate,
      inventoryOwners = [],
      loadLocations,
      locations: selected,
      products = [],
    },
    setData,
  } = useContext(SequencingWizardContext);

  const { dataCompanyId, customerIdName } = currentAffiliate;

  const handleChange = (checked, location) => {
    setData(
      R.evolve({
        action: R.always(''),
        applyOnFutureDate: R.always(null),
        futureDate: R.always(undefined),
        locations: checked ? R.append(location) : R.reject(R.equals(location)),
      })
    );
  };

  const handleSelectAllChange = (_, checked) => {
    setData(R.assoc('locations', checked ? locations : []));
  };

  useEffect(() => {
    if (loadLocations) {
      fetchLocations({
        inventoryOwners: R.pluck('companyCustomerIdName', inventoryOwners),
        products: R.pluck('id', products),
        supplier: { companyCustomerIdName: customerIdName, dataCompanyId },
      });
      setData(
        R.evolve({
          loadLocations: R.F,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCompanyId, inventoryOwners, customerIdName]);

  useEffect(() => {
    onChange(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const noSelectableLocationssMessage = (
    <Typography variant="body2">
      There are no selectable locations available for your Inventory Owner and Product selections.
    </Typography>
  );

  return (
    <>
      <Header color="textPrimary" variant="body1">
        {title}
      </Header>
      {R.isEmpty(locations) && !loadLocations && !locationsLoading ? (
        <NoneAvailableMessage message={noSelectableLocationssMessage} />
      ) : (
        <>
          <Box {...boxProps}>
            <WizardCheckbox
              checked={R.and(
                notEmpty(selected),
                R.equals(R.sortBy(R.prop('id'), locations), R.sortBy(R.prop('id'), selected))
              )}
              name="Select All Locations"
              onChange={handleSelectAllChange}
            />
          </Box>

          {R.map(
            location => (
              <Box {...boxProps} key={location.id}>
                <WizardCheckbox
                  checked={R.includes(location, selected)}
                  name={location.name}
                  onChange={(_, checked) => handleChange(checked, location)}
                />
              </Box>
            ),
            locations
          )}
        </>
      )}
    </>
  );
};

LocationCheckboxList.propTypes = {
  fetchLocations: PropTypes.func.isRequired,
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ).isRequired,
  locationsLoading: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

export default LocationCheckboxList;
